import type { LiveProject, ProjectsById } from '@wiztime/core';
import { projectHex } from '../../../lib/projectColor';
import { useEffect, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import type { DragPoint } from '../timeline/useLaneInteractions';
import styles from './Cards.module.css';

/** The card is held by its colour bar: the pointer sits this far inside its left and top edge. */
const GRAB_X_PX = 14;
const GRAB_Y_PX = 17;

/**
 * The card that follows the pointer while a quick-mark tile is dragged onto the timeline: the
 * project's colour bar and name, so it is plain that this project is being carried. It is
 * rendered to `document.body` and ignores pointer input (lane lookups see through it); the time
 * tip and the drop ghost on the lane show where the block would land.
 */
export function TileDragCard({
  project,
  projectsById,
  pointRef,
  allowed,
}: {
  project: LiveProject;
  projectsById: ProjectsById;
  /** Pointer position kept by the drag without re-rendering the tile; the card follows it itself. */
  pointRef: { readonly current: DragPoint | null };
  /** False over a lane that cannot take the block or outside every lane. */
  allowed: boolean;
}) {
  const [point, setPoint] = useState<DragPoint | null>(null);

  useEffect(() => {
    setPoint(pointRef.current);
    const onMove = (event: PointerEvent): void =>
      setPoint({ x: event.clientX, y: event.clientY, touch: false });
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [pointRef]);

  if (point === null) return null;
  return createPortal(
    <div
      className={styles.dragCard}
      style={
        {
          '--tile-color': projectHex(project, projectsById),
          left: point.x - GRAB_X_PX,
          top: point.y - GRAB_Y_PX,
        } as CSSProperties
      }
      data-testid="tile-drag-card"
      data-allowed={allowed}
    >
      <span className={styles.tileBar} aria-hidden="true" />
      <span className={styles.tileName}>{project.data.name}</span>
    </div>,
    document.body,
  );
}
